import React, { useMemo, useRef } from "react"
import { useReactToPrint } from "react-to-print"
import { useParams } from "react-router-dom"

import useAdminDocument from "../hooks/useAdminDocument.js"
import AdminStatus from "../components/AdminStatus.jsx"
import NotFound from "./NotFound.jsx"

// Quote data and payment details are confidential. They are no longer bundled:
// they are loaded from the admin API (PHP VPS) after server-side login.

// ---------- Header ----------
function Header({ title, quoteNumber }) {
  return (
    <header className="bg-[#0E1526] text-white rounded-lg p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 print:rounded-none">
      <div className="space-y-1">
        <div className="text-2xl font-semibold leading-none">
          <span className="text-white">2</span>
          <span className="text-blue-400">merative</span>
        </div>

        <div className="uppercase tracking-widest text-xs text-gray-300">
          Iterate. Create. Elevate.
        </div>
      </div>

      <div className="text-right space-y-1 md:min-w-[280px]">
        {title && <div className="text-2xl font-semibold">{title}</div>}

        {quoteNumber && (
          <div className="text-xs text-gray-300 mt-1">
            Quote #: {quoteNumber}
          </div>
        )}
      </div>
    </header>
  )
}

// ---------- Shared section heading ----------
function SectionHeading({ children }) {
  return (
    <h2 className="text-xl font-bold mb-4 border-l-4 border-blue-600 pl-3">
      {children}
    </h2>
  )
}

// ---------- Recurring Note ----------
const RecurringNote = () => (
  <p className="text-xs text-gray-500 mt-2">
    * Monthly and yearly costs are recurring charges for hosting, SSL and basic
    maintenance.{" "}
    <span className="font-semibold">
      Year 1 is included where specifically stated.
    </span>
  </p>
)

// ---------- Helpers ----------
const currency = (amount, currencyCode = "AUD") =>
  (Number(amount) || 0).toLocaleString("en-AU", {
    style: "currency",
    currency: currencyCode,
  })

const calcTotals = (sections = {}) => {
  const oneOffArr = Array.isArray(sections.oneOff)
    ? sections.oneOff
    : []

  const recurringArr = Array.isArray(sections.recurring)
    ? sections.recurring
    : []

  const sum = (arr, key) =>
    arr.reduce(
      (total, row) => total + (Number(row?.[key]) || 0),
      0
    )

  return {
    oneOff: sum(oneOffArr, "cost"),
    recurringMonthly: sum(recurringArr, "costMonthly"),
    recurringYearly: sum(recurringArr, "costYearly"),
  }
}

const getPricingTotals = (pricing = {}) => {
  const items = Array.isArray(pricing.items) ? pricing.items : []

  return items.reduce(
    (totals, item) => {
      if (item?.optional) return totals

      const amount = Number(item?.amount) || 0

      if (item?.recurring === "monthly") {
        totals.monthly += amount
      } else if (item?.recurring === "yearly") {
        totals.yearly += amount
      } else {
        totals.oneOff += amount
      }

      return totals
    },
    {
      oneOff: 0,
      monthly: 0,
      yearly: 0,
    }
  )
}


// ---------- Proposal format renderer ----------
function ProposalContent({ data }) {
  const hero = data?.hero ?? {}
  const goals = Array.isArray(data?.goals) ? data.goals : []
  const sections = Array.isArray(data?.sections)
    ? data.sections
    : []

  const pricing = data?.pricing ?? {}
  const pricingItems = Array.isArray(pricing.items)
    ? pricing.items
    : []

  const paymentSchedule = Array.isArray(
    pricing.paymentSchedule
  )
    ? pricing.paymentSchedule
    : []

  const maintenancePlans = Array.isArray(
    data?.maintenancePlans
  )
    ? data.maintenancePlans
    : []

  const faqs = Array.isArray(data?.faqs) ? data.faqs : []
  const acceptance = data?.acceptance ?? {}
  const acceptanceTerms = Array.isArray(acceptance?.terms)
    ? acceptance.terms
    : []

  const pricingTotals = getPricingTotals(pricing)
  const pricingCurrency = pricing.currency || "AUD"

  return (
    <>
      {(hero.heading || hero.intro || hero.eyebrow) && (
        <section className="p-6 border-t border-gray-200 bg-gray-50">
          {hero.eyebrow && (
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-blue-700 mb-2">
              {hero.eyebrow}
            </div>
          )}

          {hero.heading && (
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
              {hero.heading}
            </h1>
          )}

          {hero.intro && (
            <p className="mt-4 text-base text-gray-700 leading-7 max-w-4xl">
              {hero.intro}
            </p>
          )}

          {hero.contact && (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
              <div>
                {hero.contact.agencyName && (
                  <div className="font-semibold text-gray-800">
                    {hero.contact.agencyName}
                  </div>
                )}

                {hero.contact.contactName && (
                  <div>{hero.contact.contactName}</div>
                )}
              </div>

              <div className="sm:text-right">
                {hero.contact.email && (
                  <div>
                    <a
                      href={`mailto:${hero.contact.email}`}
                      className="text-blue-600 underline"
                    >
                      {hero.contact.email}
                    </a>
                  </div>
                )}

                {hero.contact.phone && (
                  <div>
                    <a
                      href={`tel:${hero.contact.phone.replace(
                        /\s+/g,
                        ""
                      )}`}
                      className="text-blue-600 underline"
                    >
                      {hero.contact.phone}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {goals.length > 0 && (
        <section className="p-6 border-t border-gray-200">
          <SectionHeading>Project goals</SectionHeading>

          <ul className="list-disc ml-5 space-y-2 text-sm text-gray-700">
            {goals.map((goal, index) => (
              <li key={`goal-${index}`}>{goal}</li>
            ))}
          </ul>
        </section>
      )}

      {sections.map((section, sectionIndex) => {
        const paragraphs = Array.isArray(section?.paragraphs)
          ? section.paragraphs
          : []

        const bullets = Array.isArray(section?.bullets)
          ? section.bullets
          : []

        return (
          <section
            key={section?.id || `section-${sectionIndex}`}
            id={section?.id}
            className="p-6 border-t border-gray-200 break-inside-avoid"
          >
            <SectionHeading>
              {section?.title || "Project detail"}
            </SectionHeading>

            {paragraphs.length > 0 && (
              <div className="space-y-3 text-sm text-gray-700 leading-6">
                {paragraphs.map((paragraph, index) => (
                  <p
                    key={`${section?.id || sectionIndex}-paragraph-${index}`}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            )}

            {bullets.length > 0 && (
              <ul
                className={`list-disc ml-5 space-y-2 text-sm text-gray-700 ${
                  paragraphs.length > 0 ? "mt-4" : ""
                }`}
              >
                {bullets.map((bullet, index) => (
                  <li
                    key={`${section?.id || sectionIndex}-bullet-${index}`}
                  >
                    {bullet}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )
      })}

      {pricingItems.length > 0 && (
        <section className="p-6 border-t border-gray-200">
          <SectionHeading>Investment</SectionHeading>

          {pricing.disclaimer && (
            <p className="text-sm text-gray-600 mb-5">
              {pricing.disclaimer}
            </p>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-3 pr-4">Item</th>
                  <th className="py-3 text-right whitespace-nowrap">
                    Investment
                  </th>
                </tr>
              </thead>

              <tbody>
                {pricingItems.map((item, index) => {
                  let amountLabel = currency(
                    item?.amount,
                    pricingCurrency
                  )

                  if (item?.amount === 0) {
                    amountLabel = item?.optional
                      ? "Optional"
                      : "Included"
                  }

                  if (item?.recurring === "monthly") {
                    amountLabel = `${currency(
                      item?.amount,
                      pricingCurrency
                    )} / month`
                  }

                  if (item?.recurring === "yearly") {
                    amountLabel = `${currency(
                      item?.amount,
                      pricingCurrency
                    )} / year`
                  }

                  return (
                    <tr
                      key={`pricing-${index}`}
                      className="border-b last:border-0 align-top"
                    >
                      <td className="py-4 pr-4">
                        <div className="font-semibold text-gray-800">
                          {item?.label || "Project item"}
                        </div>

                        {item?.description && (
                          <div className="text-gray-600 mt-1 leading-5">
                            {item.description}
                          </div>
                        )}

                        {item?.optional && (
                          <div className="text-xs text-blue-700 font-semibold mt-1">
                            Optional inclusion
                          </div>
                        )}
                      </td>

                      <td className="py-4 text-right font-semibold whitespace-nowrap">
                        {amountLabel}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {pricing.showTotals !== false && (
            <div className="mt-6 ml-auto max-w-md bg-gray-50 p-4 rounded-lg text-sm">
              <h3 className="font-semibold text-blue-700 mb-3">
                Total investment
              </h3>

              {pricingTotals.oneOff > 0 && (
                <div className="flex justify-between gap-4 py-1">
                  <span>One-off project total</span>
                  <span className="font-semibold">
                    {currency(
                      pricingTotals.oneOff,
                      pricingCurrency
                    )}
                  </span>
                </div>
              )}

              {pricingTotals.monthly > 0 && (
                <div className="flex justify-between gap-4 py-1">
                  <span>Monthly recurring</span>
                  <span className="font-semibold">
                    {currency(
                      pricingTotals.monthly,
                      pricingCurrency
                    )}
                  </span>
                </div>
              )}

              {pricingTotals.yearly > 0 && (
                <div className="flex justify-between gap-4 py-1">
                  <span>Yearly recurring</span>
                  <span className="font-semibold">
                    {currency(
                      pricingTotals.yearly,
                      pricingCurrency
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          {paymentSchedule.length > 0 && (
            <div className="mt-8">
              <h3 className="font-semibold text-gray-700 mb-3 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
                Payment schedule
              </h3>

              <div className="space-y-3">
                {paymentSchedule.map((payment, index) => {
                  const milestoneAmount =
                    pricingTotals.oneOff > 0 &&
                    Number(payment?.percentage) > 0
                      ? pricingTotals.oneOff *
                        (Number(payment.percentage) / 100)
                      : 0

                  return (
                    <div
                      key={`payment-${index}`}
                      className="border border-gray-200 rounded-lg p-4 text-sm"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div>
                          <div className="font-semibold text-gray-800">
                            {payment?.label || "Payment milestone"}
                          </div>

                          {payment?.detail && (
                            <div className="text-gray-600 mt-1">
                              {payment.detail}
                            </div>
                          )}
                        </div>

                        <div className="sm:text-right whitespace-nowrap">
                          {payment?.percentage && (
                            <div className="font-semibold text-blue-700">
                              {payment.percentage}%
                            </div>
                          )}

                          {milestoneAmount > 0 && (
                            <div className="text-gray-700">
                              {currency(
                                milestoneAmount,
                                pricingCurrency
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {maintenancePlans.length > 0 && (
        <section className="p-6 border-t border-gray-200">
          <SectionHeading>
            Hosting, maintenance and support
          </SectionHeading>

          <div className="grid grid-cols-1 gap-4">
            {maintenancePlans.map((plan, index) => {
              const includes = Array.isArray(plan?.includes)
                ? plan.includes
                : []

              return (
                <div
                  key={`maintenance-${index}`}
                  className="border border-gray-200 rounded-lg p-5 break-inside-avoid"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {plan?.name || "Maintenance plan"}
                      </h3>

                      {plan?.tagline && (
                        <p className="text-sm text-gray-600 mt-1">
                          {plan.tagline}
                        </p>
                      )}
                    </div>

                    {plan?.priceLabel && (
                      <div className="text-blue-700 font-semibold whitespace-nowrap">
                        {plan.priceLabel}
                      </div>
                    )}
                  </div>

                  {includes.length > 0 && (
                    <ul className="list-disc ml-5 mt-4 space-y-2 text-sm text-gray-700">
                      {includes.map((item, itemIndex) => (
                        <li
                          key={`maintenance-${index}-${itemIndex}`}
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            })}
          </div>
        </section>
      )}

      {faqs.length > 0 && (
        <section className="p-6 border-t border-gray-200">
          <SectionHeading>
            Frequently asked questions
          </SectionHeading>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <article
                key={`faq-${index}`}
                className="border border-gray-200 rounded-lg p-5 break-inside-avoid"
              >
                <h3 className="font-semibold text-gray-800">
                  {faq?.question || "Question"}
                </h3>

                {faq?.answer && (
                  <p className="text-sm text-gray-700 leading-6 mt-2">
                    {faq.answer}
                  </p>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {(acceptance.heading ||
        acceptance.blurb ||
        acceptanceTerms.length > 0) && (
        <section className="p-6 border-t border-gray-200">
          <SectionHeading>
            {acceptance.heading || "Next steps"}
          </SectionHeading>

          {acceptance.blurb && (
            <p className="text-sm text-gray-700 leading-6">
              {acceptance.blurb}
            </p>
          )}

          {acceptanceTerms.length > 0 && (
            <ul className="list-disc ml-5 mt-4 space-y-2 text-sm text-gray-700">
              {acceptanceTerms.map((term, index) => (
                <li key={`acceptance-term-${index}`}>
                  {term}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  )
}

// ---------- Legacy quote format renderer ----------
function LegacyQuoteContent({ data }) {
  const notes = Array.isArray(data?.notes) ? data.notes : []

  const assumptions = Array.isArray(
    data?.assumptionsExclusions
  )
    ? data.assumptionsExclusions
    : []

  const warranty = Array.isArray(data?.warrantySupport)
    ? data.warrantySupport
    : []

  const options = Array.isArray(data?.options)
    ? data.options
    : []

  const totals = options.map((option) => ({
    label: option?.label ?? "Untitled Option",
    ...calcTotals(option?.sections),
  }))

  return (
    <>
      {options.map((option, optionIndex) => {
        const optionTotals = totals[optionIndex]

        const oneOff = Array.isArray(
          option?.sections?.oneOff
        )
          ? option.sections.oneOff
          : []

        const recurring = Array.isArray(
          option?.sections?.recurring
        )
          ? option.sections.recurring
          : []

        const approach = Array.isArray(
          option?.sections?.projectApproach
        )
          ? option.sections.projectApproach
          : []

        return (
          <section
            key={option?.label ?? optionIndex}
            className="p-6 border-t border-gray-200"
          >
            <SectionHeading>
              {option?.label ?? "Untitled Option"}
            </SectionHeading>

            {approach.length > 0 && (
              <div className="mb-4 text-sm">
                <h3 className="font-semibold text-gray-700 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
                  Project approach
                </h3>

                <ul className="list-disc ml-5 space-y-1">
                  {approach.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {oneOff.length > 0 && (
              <>
                <h3 className="font-semibold text-gray-700 mt-4 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
                  One-off costs
                </h3>

                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="py-2">Item</th>
                      <th className="py-2 text-right">
                        Cost
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {oneOff.map((row, index) => (
                      <tr
                        key={index}
                        className="border-b last:border-0"
                      >
                        <td className="py-2">
                          {row?.item ?? "Untitled"}
                        </td>

                        <td className="py-2 text-right">
                          {currency(row?.cost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {recurring.length > 0 && (
              <>
                <h3 className="font-semibold text-gray-700 mt-6 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
                  Recurring costs
                </h3>

                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="py-2">Item</th>
                      <th className="py-2 text-right">
                        Monthly
                      </th>
                      <th className="py-2 text-right">
                        Yearly
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {recurring.map((row, index) => (
                      <tr
                        key={index}
                        className="border-b last:border-0"
                      >
                        <td className="py-2">
                          {row?.item ?? "Untitled"}
                        </td>

                        <td className="py-2 text-right">
                          {row?.costMonthly
                            ? currency(row.costMonthly)
                            : "-"}
                        </td>

                        <td className="py-2 text-right">
                          {row?.costYearly
                            ? currency(row.costYearly)
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <RecurringNote />
              </>
            )}

            <div className="mt-6 bg-gray-50 p-4 rounded shadow-sm text-sm">
              <h3 className="font-semibold mb-2 text-blue-700">
                Totals
              </h3>

              <div className="flex justify-between">
                <span>One-off:</span>
                <span>{currency(optionTotals.oneOff)}</span>
              </div>

              <div className="flex justify-between">
                <span>Monthly:</span>
                <span>
                  {currency(optionTotals.recurringMonthly)}
                </span>
              </div>

              <div className="flex justify-between">
                <span>Yearly:</span>
                <span>
                  {currency(optionTotals.recurringYearly)}
                </span>
              </div>
            </div>
          </section>
        )
      })}

      {notes.length > 0 && (
        <ListSection title="Notes" items={notes} />
      )}

      {assumptions.length > 0 && (
        <ListSection
          title="Assumptions & Exclusions"
          items={assumptions}
        />
      )}

      {warranty.length > 0 && (
        <ListSection
          title="Warranty & Support"
          items={warranty}
        />
      )}
    </>
  )
}

function ListSection({ title, items }) {
  return (
    <section className="p-6 border-t border-gray-200 text-sm">
      <h3 className="font-semibold text-gray-700 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
        {title}
      </h3>

      <ul className="list-disc ml-5 space-y-1">
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    </section>
  )
}

// ---------- Main ----------
function QuoteDocument({ data, payment = {} }) {
  const contentRef = useRef(null)

  const meta = data?.meta ?? {}
  const business = data?.business ?? {}
  const heroContact = data?.hero?.contact ?? {}
  const acceptance = data?.acceptance ?? {}

  const isProposalFormat = useMemo(
    () =>
      Boolean(data?.hero) ||
      Array.isArray(data?.sections) ||
      Boolean(data?.pricing),
    [data]
  )

  const handlePrint = useReactToPrint({
    documentTitle:
      meta.quoteNumber || meta.title || "Quote",
    contentRef,
    removeAfterPrint: true,
  })

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div
        ref={contentRef}
        className="bg-white rounded-lg shadow print:shadow-none"
      >
        <Header
          title={meta.title}
          quoteNumber={meta.quoteNumber}
        />

        {Array.isArray(meta.summary) &&
          meta.summary.length > 0 && (
            <section className="p-6 border-b border-gray-200 text-sm">
              <h2 className="font-semibold text-gray-700 mb-3">
                Summary
              </h2>

              <div className="space-y-3 text-gray-700 leading-6">
                {meta.summary.map((paragraph, index) => (
                  <p
                    key={index}
                    dangerouslySetInnerHTML={{
                      __html: paragraph,
                    }}
                  />
                ))}
              </div>
            </section>
          )}

        <section className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <h2 className="font-semibold text-gray-700 mb-2">
              Prepared for
            </h2>

            {meta.clientName && (
              <div>{meta.clientName}</div>
            )}

            {meta.siteDomain && (
              <div>{meta.siteDomain}</div>
            )}

            {meta.url && (
              <div>
                <a
                  href={meta.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline"
                >
                  {meta.url}
                </a>
              </div>
            )}

            {meta.quoteNumber && (
              <div>Quote #: {meta.quoteNumber}</div>
            )}

            {meta.date && <div>Date: {meta.date}</div>}
          </div>

          <div className="md:text-right">
            <h2 className="font-semibold text-gray-700 mb-2">
              Prepared by
            </h2>

            {(business.name || heroContact.agencyName) && (
              <div>
                {business.name || heroContact.agencyName}
              </div>
            )}

            {business.abn && <div>ABN: {business.abn}</div>}

            {(business.email || heroContact.email) && (
              <div>{business.email || heroContact.email}</div>
            )}

            {(business.phone || heroContact.phone) && (
              <div>{business.phone || heroContact.phone}</div>
            )}

            {meta.preparedBy && (
              <div>Consultant: {meta.preparedBy}</div>
            )}

            {meta.auditedBy && (
              <div>Audited by: {meta.auditedBy}</div>
            )}
          </div>
        </section>

        {isProposalFormat ? (
          <ProposalContent data={data} />
        ) : (
          <LegacyQuoteContent data={data} />
        )}

        <section className="p-6 border-t border-gray-200 text-sm break-inside-avoid">
          <h2 className="font-semibold text-gray-700 mb-3 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
            Acceptance & payment details
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-1">
            <div>
              <strong>Bank:</strong> {payment.bankName}
            </div>

            <div>
              <strong>Account Name:</strong> {payment.accountName}
            </div>

            <div>
              <strong>BSB:</strong> {payment.bsb}
            </div>

            <div>
              <strong>Account Number:</strong> {payment.accountNumber}
            </div>

            <div className="sm:col-span-2">
              <strong>Reference:</strong> {payment.referencePrefix}{" "}
              {meta.quoteNumber}
            </div>

            {(acceptance?.validUntil ||
              acceptance?.paymentTerms) && (
              <div className="sm:col-span-2 mt-2 border-t pt-2">
                {acceptance?.validUntil && (
                  <div>
                    <strong>Valid until:</strong>{" "}
                    {acceptance.validUntil}
                  </div>
                )}

                {acceptance?.paymentTerms && (
                  <div>
                    <strong>Payment terms:</strong>{" "}
                    {acceptance.paymentTerms}
                  </div>
                )}
              </div>
            )}
          </div>

          <p className="mt-4 text-gray-600">
            Acceptance and project scheduling will be
            confirmed once the deposit payment has been
            received. A full invoice will be issued for your
            records.
          </p>
        </section>
      </div>

      <div className="mt-6 flex justify-end print:hidden">
        <button
          type="button"
          onClick={handlePrint}
          className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
        >
          Print / Save as PDF
        </button>
      </div>
    </div>
  )
}

// Loads the quote (by URL slug; aliases resolve on the server) and the payment
// details, then renders the unchanged quote document. Unknown slugs show a
// proper Not Found page instead of falling back to another client's quote.
export default function Quote({ slug: slugOverride }) {
  const params = useParams()
  const slug = (slugOverride || params.slug || "").toLowerCase()
  const quote = useAdminDocument("quote", slug)
  const business = useAdminDocument("business")

  if (quote.status === "not-found") {
    return <NotFound title="Quote not found" message="This quote does not exist or is no longer available." />
  }
  if (quote.status === "error" || business.status === "error") {
    return <AdminStatus message="Unable to load this quote." detail={quote.error || business.error} />
  }
  if (quote.status !== "ready" || business.status !== "ready") {
    return <AdminStatus message="Loading quote…" />
  }
  return <QuoteDocument data={quote.data} payment={business.data?.quotePayment || {}} />
}
